import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Form } from "antd";
import dayjs from "dayjs";

import QuotationService from "../../../../../../../../../shared/services/quotation";

import { StepThreeFormContext } from "./StepThreeFormContext";
import { StepTwoFormContext } from "../../../StepTwo/contexts/StepTwoForm";
import { WizardDataFormContext } from "../../../../Wizard/contexts/WizardDataForm/WizardDataFormContext";

import { DataQuotation, InfoTable } from "../../interfaces";
import { DECISION_TYPE, GetCommertialsConditionsResponse, SelectOption } from "../../../../../../../../../shared/interfaces";

import { formattedPrice, scrollToEmitButton } from "../../../../../../../../../shared/utils";
import { GenericDataFormContext, LOADING_SAVED_QUOTATIONS_STATES, QUOTATION_BUTTON, QuotationButton, QuotationInfoContext, QuotationStep } from "../../../../../shared";
import { StepThreeForm } from "../../../../../utils";

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [stepThreeForm] = Form.useForm<StepThreeForm>();

  const { paymentMethodsList, setCanShowSpinner } = useContext(GenericDataFormContext);
  const { currentStep, quotationInfo, loadSaveQuotationState, setLoadSaveQuotationState, savedQuotation } = useContext(QuotationInfoContext);
  const { quotationForm } = useContext(WizardDataFormContext);
  const { stepTwoForm } = useContext(StepTwoFormContext);


  const [optionsPaymentMethod, setOptionsPaymentMethod] = useState<Array<SelectOption>>([]);
  const [isPersonalDomestic, setIsPersonalDomestic] = useState<boolean>(false);
  const [primaData, setPrimaData] = useState<string>("");
  const [sumInsuredByPerson, setSumInsuredByPerson] = useState<InfoTable[]>([]);
  const [rightEmissionLife, setRightEmissionLife] = useState<InfoTable[]>([]);
  const [commertialConditions, setCommertialConditions] = useState<GetCommertialsConditionsResponse>();

  const [quotationResponse, setQuotationResponse] = useState<DataQuotation>();
  const [labelButton, setLabelButton] = useState<QuotationButton>(quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR);

  const getSumInsuredByPerson = async (_isPersonalDomestic: boolean) => {
    try {
      const response = await QuotationService.getLifeSumObligatory();

      const {
        sumaPersonalDomestico,
        premioPersonalDomestico,
        sumaPolizaSinNomina,
        premioPolizaSinNomina,
      } = response?.data;

      setPrimaData(
        _isPersonalDomestic ? premioPersonalDomestico : premioPolizaSinNomina
      );

      const infoTable = [
        {
          key: '1',
          sumAssured: `$ ${_isPersonalDomestic
            ? formattedPrice(sumaPersonalDomestico)
            : formattedPrice(sumaPolizaSinNomina)
            }`,
          prima: `$ ${_isPersonalDomestic
            ? formattedPrice(premioPersonalDomestico)
            : formattedPrice(premioPolizaSinNomina)
            }`,
        },
      ];
      setSumInsuredByPerson(infoTable);
    } catch (error) { }
  };

  const getEmissionLife = async () => {
    try {
      const response = await QuotationService?.getEmissionRightLifeObligatory();

      const dataFormatted =
        response?.data?.valoresDerechoEmision?.valorDerechoEmision?.map(
          (item, index) => ({
            key: (index + 1).toString(),
            sumAssured: item?.descripcion?.toUpperCase(),
            prima: `$ ${parseFloat(item?.importe).toFixed(2)}`,
          })
        );

      setRightEmissionLife(dataFormatted);
    } catch (error) { }
  };

  const getCommertialConditions = useCallback(async () => {
    try {
      const response = await QuotationService.getComertialsConditions(quotationInfo?.branch!);

      if (response.data) {
        setCommertialConditions(response.data);
      }
    } catch (error) { }
  }, [quotationInfo, setCommertialConditions]);

  const getPaymentMethodsList = useCallback(() => {
    if (paymentMethodsList?.length) {
      const paymentMethods: Array<SelectOption> = paymentMethodsList.map(
        ({ descripcion, nroPlanDePago, codigoFormaDePago, esDefault }) => ({
          label: descripcion,
          value: `${codigoFormaDePago}-${nroPlanDePago}`,
          nroPlanDePago: nroPlanDePago,
          codigoFormaDePago: codigoFormaDePago,
          esDefault: esDefault,
        })
      );
      setOptionsPaymentMethod(paymentMethods);
    }
  }, [paymentMethodsList, setOptionsPaymentMethod]);

  const getPaymentMethodDefault = useCallback(() => {
    if (optionsPaymentMethod.length) {
      const defaultOption = optionsPaymentMethod.find(
        (option) => option.esDefault === DECISION_TYPE.YES
      )?.value;
      return defaultOption
    }
  }, [paymentMethodsList, optionsPaymentMethod]);

  
  const sendQuotationLifeObligatory = useCallback(async () => {
    setCanShowSpinner(true);
    const clientSended = {
      nroAsegurado: Number(quotationForm?.cliente?.nroAsegurado),
      tipoDocumento: quotationForm?.cliente?.tipoDocumento,
      nroDocumento: Number(quotationForm?.cliente?.nroDocumento),
      nombre: quotationForm?.cliente?.nombre,
      cuit: Number(quotationForm?.cliente?.cuit),
      tipoPersona: quotationForm?.cliente?.tipoPersona,
      codProvincia: quotationForm?.cliente?.codProvincia,
      codProvinciaInder: Number(quotationForm?.cliente?.codProvinciaInder),
      codigoPostal: Number(quotationForm?.cliente?.codigoPostal),
      sufijoCodigoPostal: Number(quotationForm?.cliente?.sufijoCodigoPostal),
      condicionIVA: Number(quotationForm?.cliente?.condicionIVA),
    };

    const paymentMethodCode = stepThreeForm?.getFieldInstance('paymentMethod') ? stepThreeForm?.getFieldValue('paymentMethod').split('-')[1] : getPaymentMethodDefault()?.split('-')[1];
    
    const params = {
      numeroCotizacion: Number(quotationInfo?.token),
      cliente: clientSended,
      rama: quotationInfo?.branch,
      articuloRamaSecuencia: Number(quotationInfo?.arse),
      planDePago: paymentMethodCode,
      esPersonalDomestico: stepTwoForm?.getFieldValue('personalDomestic') === DECISION_TYPE.YES,
      cantidad: stepTwoForm?.getFieldValue('quantityInsured'),
      tipoDeFacturacion: stepTwoForm?.getFieldValue('billingPeriod'),
      extraPrimaVariable: commertialConditions?.porcentajeExtraPrimaVariable,
      fechaDeInicio: dayjs(stepTwoForm?.getFieldValue('vigencyDate'), 'YYYYMMDD').format(
        'DD-MM-YYYY'
      ),
    };

    try {
      const response = await QuotationService.postQuotationLifeObligatory(params);
      return response;
    } catch (error) {} finally {
      setCanShowSpinner(false);
    }
  }, [
    quotationInfo,
    stepTwoForm,
    stepThreeForm,
    getPaymentMethodDefault,
    commertialConditions,
    paymentMethodsList,
    optionsPaymentMethod,
    setCanShowSpinner,
  ]);
  
  const getDataTables = useCallback(async () => {
    setCanShowSpinner(true);

    const isPersonalDomestic = stepTwoForm?.getFieldValue('personalDomestic') === DECISION_TYPE.YES;

    await Promise.all([
      getEmissionLife(),
      getSumInsuredByPerson(isPersonalDomestic),
      getCommertialConditions(),
    ]).finally(() => {
      setCanShowSpinner(false);
    });

  }, [
    setCanShowSpinner,
    stepTwoForm,
    getEmissionLife,
    getSumInsuredByPerson,
    getCommertialConditions,
  ]);

  const fillStepThreeServices = useCallback(async () => {
    try {
      await getCommertialConditions();
      await getEmissionLife();
      await getSumInsuredByPerson(stepTwoForm?.getFieldValue('personalDomestic') === DECISION_TYPE.YES);
    } catch (error) {}
  }, [
    getCommertialConditions,
    getEmissionLife,
    getSumInsuredByPerson,
  ]);

  const handleOnClickQuotation = useCallback(async () => {
    setCanShowSpinner(true);
    try {
      const response = await sendQuotationLifeObligatory();
      
      if (response?.data) {
        setQuotationResponse(response.data);
        scrollToEmitButton();
        setLabelButton(QUOTATION_BUTTON.EMITIR);
      };
    } catch (error) {} finally {
      setCanShowSpinner(false);
    }
  },[
    setQuotationResponse, 
    sendQuotationLifeObligatory,
    setLabelButton,
    setCanShowSpinner,
  ]);
  
  const fillStepThreeData = useCallback(async () => {
    if (stepTwoForm?.getFieldValue('personalDomestic') === DECISION_TYPE.YES) {
      stepThreeForm.setFields([
        { name: 'paymentMethod', value: `${savedQuotation.formaDePago.codigo}-${savedQuotation.formaDePago.codigoDePlanDePagos}`}
      ]);
      setIsPersonalDomestic(true);
    } else {
      setIsPersonalDomestic(false);
    };

    setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED);
  }, [
    savedQuotation,
    stepThreeForm,
    setLabelButton,
    setIsPersonalDomestic,
    setLoadSaveQuotationState,
  ]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING && paymentMethodsList.length) {
      getPaymentMethodsList();
    }
  }, [loadSaveQuotationState, paymentMethodsList]);

  useEffect(() => {
    if(currentStep === QuotationStep.THREE && !loadSaveQuotationState) {
      getPaymentMethodsList();
      getDataTables();
    }
  }, [
    currentStep, 
    loadSaveQuotationState, 
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING);
      fillStepThreeServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING &&
      paymentMethodsList.length &&
      optionsPaymentMethod.length &&
      commertialConditions &&
      rightEmissionLife.length &&
      sumInsuredByPerson.length &&
      primaData !== ''
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED);
    }
  }, [
    loadSaveQuotationState,
    paymentMethodsList,
    optionsPaymentMethod,
    commertialConditions,
    rightEmissionLife,
    sumInsuredByPerson,
    primaData,
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING);
      fillStepThreeData();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING) {
      handleOnClickQuotation();
    }
  }, [loadSaveQuotationState]);

  const values = useMemo(() => ({
    stepThreeForm,
    optionsPaymentMethod,
    isPersonalDomestic,
    setIsPersonalDomestic,
    primaData,
    setPrimaData,
    sumInsuredByPerson,
    setSumInsuredByPerson,
    rightEmissionLife,
    setRightEmissionLife,
    commertialConditions,
    getSumInsuredByPerson,
    getEmissionLife,
    getCommertialConditions,
    quotationResponse,
    setQuotationResponse,
    labelButton,
    setLabelButton,
    getPaymentMethodDefault,
    getPaymentMethodsList,
    sendQuotationLifeObligatory,
    handleOnClickQuotation,
  }), [
    stepThreeForm,
    optionsPaymentMethod,
    isPersonalDomestic,
    setIsPersonalDomestic,
    primaData,
    setPrimaData,
    sumInsuredByPerson,
    setSumInsuredByPerson,
    commertialConditions,
    getSumInsuredByPerson,
    getEmissionLife,
    getCommertialConditions,
    quotationResponse,
    setQuotationResponse,
    labelButton,
    setLabelButton,
    getPaymentMethodDefault,
    getPaymentMethodsList,
    sendQuotationLifeObligatory,
    handleOnClickQuotation
  ]);

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  )
}